import { useAppSelector } from 'app/config/storeConfig/hooks';
import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import { Shape as KonvaShape } from 'konva/lib/Shape';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { getShowGrid } from '../../model/selectors/getShowGrid';
import { getSnapStepPixels } from '../../model/selectors/getSnapStepPixels';

interface HookApi {
  gridRef: MutableRefObject<Nullable<Konva.Layer>>;
  onDragMove: (e: KonvaEventObject<MouseEvent>) => void;
}

// Minimum on-screen distance between visible dots. When the user-chosen
// snap step would render dots closer than this, we draw every Nth snap
// point instead of every one, keeping render cost roughly constant
// regardless of how fine the snap step is.
const MIN_DISPLAY_STEP_PX = 16;
const DOT_SIZE_SCREEN_PX = 2;
const DOT_COLOR = '#8a96a3';

export const useCanvasGrid = (
  { width, height }: { width: number; height: number },
  canvasScale: number,
  stageRef: MutableRefObject<Nullable<Konva.Stage>>,
  canvasPosition: { x: number; y: number },
): HookApi => {
  const gridRef = useRef<Konva.Layer>(null);
  const gridShapeRef = useRef<Nullable<KonvaShape>>(null);

  const snapStepPixels = useAppSelector(getSnapStepPixels);
  const showGrid = useAppSelector(getShowGrid);

  const onDragMove = useCallback(
    (e: KonvaEventObject<MouseEvent>): void => {
      e.target.position({
        x: Math.round(e.target.x() / snapStepPixels) * snapStepPixels,
        y: Math.round(e.target.y() / snapStepPixels) * snapStepPixels,
      });

      stageRef.current?.batchDraw();
    },
    [snapStepPixels, stageRef],
  );

  useEffect(() => {
    const layer = gridRef.current;
    if (!layer) return;

    // Lazily allocate the grid shape once and mutate it on subsequent
    // renders. Avoids a `destroyChildren` + `new KonvaShape` per pan tick.
    let gridShape = gridShapeRef.current;
    if (!gridShape) {
      gridShape = new KonvaShape({
        fill: DOT_COLOR,
        listening: false,
        perfectDrawEnabled: false,
      });
      gridShapeRef.current = gridShape;
      layer.add(gridShape);
    }

    if (!showGrid || width <= 0 || height <= 0 || canvasScale <= 0 || snapStepPixels <= 0) {
      gridShape.visible(false);
      layer.batchDraw();
      return;
    }

    // De-densify: draw every Nth snap point so on-screen spacing stays
    // >= MIN_DISPLAY_STEP_PX. Keeps perf roughly constant even at very
    // fine snap steps (e.g. step=10).
    const effectiveSnapPx = snapStepPixels * canvasScale;
    const stride = Math.max(1, Math.ceil(MIN_DISPLAY_STEP_PX / effectiveSnapPx));
    const displayStepWorld = snapStepPixels * stride;

    // Visible window in world coordinates.
    const worldMinX = -canvasPosition.x / canvasScale;
    const worldMinY = -canvasPosition.y / canvasScale;
    const worldMaxX = worldMinX + width / canvasScale;
    const worldMaxY = worldMinY + height / canvasScale;

    const startX = Math.floor(worldMinX / displayStepWorld) * displayStepWorld;
    const startY = Math.floor(worldMinY / displayStepWorld) * displayStepWorld;

    // Draw dots as small screen-pixel squares: `fillRect` is roughly an
    // order of magnitude faster than `arc()` for thousands of points.
    const dotSizeWorld = DOT_SIZE_SCREEN_PX / canvasScale;
    const dotOffsetWorld = dotSizeWorld / 2;

    gridShape.visible(true);
    gridShape.sceneFunc((context, shape) => {
      const ctx = context._context;
      ctx.fillStyle = DOT_COLOR;
      for (let x = startX; x <= worldMaxX; x += displayStepWorld) {
        for (let y = startY; y <= worldMaxY; y += displayStepWorld) {
          // Center the dot on the snap point so it sits exactly on the grid line.
          ctx.fillRect(x - dotOffsetWorld, y - dotOffsetWorld, dotSizeWorld, dotSizeWorld);
        }
      }
      // Required so Konva can hit-test / cache properly even though we
      // draw directly to the underlying 2D context above.
      context.fillStrokeShape(shape);
    });

    layer.batchDraw();
  }, [width, height, canvasScale, canvasPosition.x, canvasPosition.y, snapStepPixels, showGrid]);

  useEffect(() => {
    return () => {
      gridShapeRef.current?.destroy();
      gridShapeRef.current = null;
    };
  }, []);

  return {
    gridRef,
    onDragMove,
  };
};
